import _ from 'lodash';
import { createSelector, createStructuredSelector } from 'reselect';

import { getAppSettings } from './AppConfigsSelectors';
import { getIsArchiveMode } from './AuthSelectors';
import { SMART_DOCUMENTS_ENTITIES } from '../constants/MasterSetupConstants';
import { ADMIN_NAVIGATION_ITEMS } from '../constants/NavigationConstants';
import { FORM_KEYS } from '../form-designer/constants/form-manager.constants';
import {
  getComponentProps,
  getEntityData,
  getEntityDefinition,
  getEntityType,
  getFormEntityKey,
  getTranslations,
  getUserRights,
} from '../form-designer/selectors/common.selectors';
import { getClientChanges } from '../form-designer/selectors/runtime.selectors';
import {
  getFilteredNavigationLink,
  getSelectedNavLink,
  shouldSkipRootMenuItem,
} from '../utils/SideNavigationUtils';

export const getMasterSetupPage = (state) => state.masterSetupPage;

export const getSavedSearchResults = (state) => state.savedSearchResults;

export const getNavigation = (state) => state.navigation;

export const getAvailableNavigation = createSelector(
  [getNavigation, getAppSettings, getUserRights, getIsArchiveMode],
  (navigation, appSettings, userRights, isArchiveMode) =>
    navigation.reduce((acc, navigationItem) => {
      if (shouldSkipRootMenuItem(navigationItem, { isArchiveMode, appSettings })) {
        return acc;
      }

      if (ADMIN_NAVIGATION_ITEMS.includes(navigationItem.Id)) {
        const filteredNavLink = getFilteredNavigationLink(navigationItem, {
          userRights,
          appSettings,
        });

        return [...acc, filteredNavLink];
      }

      return [...acc, navigationItem];
    }, [])
);

export const getIsAdminNavigationAvailable = createSelector([getNavigation], (navigation) => {
  const adminNavigation = navigation.find(
    (navigationItem) => navigationItem.Id === 'ModuleAdminMenu'
  );

  return adminNavigation !== undefined;
});

export const getWarnings = (state) => state.warnings;

export const getIsDrawerOpen = createSelector(
  getMasterSetupPage,
  (masterSetupPage) => masterSetupPage?.isDrawerOpen
);

export const getRedirect = (state) => state.masterSetupPage?.redirect;

export const getIsAddRecordButtonEnabled = (state, props) => {
  const { entityName } = getMasterSetupPage(state, props);
  const navigation = getNavigation(state, props);

  const navLink = getSelectedNavLink(navigation, { entityName });

  if (navLink) {
    return ![
      'MasterSetupSearchDisableAdd',
      'MasterSetupOptimizedSearchDisableAdd',
      'WorkflowBasedMasterSetupSearchDisableAdd',
    ].includes(navLink.Type);
  }

  return false;
};

export const getIsBulkImportEnabled = (state, props) => {
  const { entityName } = getMasterSetupPage(state, props);
  const navigation = getNavigation(state, props);

  const navLink = getSelectedNavLink(navigation, { entityName });

  if (navLink) {
    return ![
      'MasterSetupDisableBulkImport',
      'MasterSetupOptimizedSearchDisableBulkImport',
      'WorkflowBasedMasterSetupSearchDisableBulkImport',
    ].includes(navLink.Type);
  }

  return false;
};

// TODO: refactor (remove hardoced logic)
export const getEntityName = (state) => {
  const masterSetupPage = getMasterSetupPage(state);
  const entityName = getFormEntityKey(state, { formKey: FORM_KEYS.STEP });

  return entityName ?? masterSetupPage.entityName;
};

export const getSearchName = createSelector(
  getMasterSetupPage,
  (masterSetupPage) => masterSetupPage?.searchName
);

export const getSavedSearchParams = (savedSearchResultsKey) =>
  createSelector(
    getSavedSearchResults,
    (savedSearchResults) => savedSearchResults[savedSearchResultsKey]?.searchParams
  );

// TODO: refactor (remove hardoced logic)
export const getItemId = (state) => {
  const entityData = getEntityData(state, { formKey: FORM_KEYS.STEP });
  const entityDefinition = getEntityDefinition(state, { formKey: FORM_KEYS.STEP });
  const entityType = getEntityType(state, { formKey: FORM_KEYS.STEP });

  const key = entityDefinition[entityType]?.properties.PrimaryKey;
  return entityData[entityType][key];
};

export const getSaveParameters = createStructuredSelector({
  entityName: getEntityName,
  itemId: getItemId,
  clientChanges: getClientChanges,
});

export const getDeleteParameters = createStructuredSelector({
  entityName: getEntityName,
  itemId: getItemId,
});

export const getRecordSaveSuccessTranslation = createSelector(getTranslations, (translations) =>
  _.get(translations, ['Form', 'RecordSaveSuccess'], 'RecordSaveSuccess')
);

export const getRecordDeleteSuccessTranslation = createSelector(getTranslations, (translations) =>
  _.get(translations, ['Form', 'RecordDeleteSuccess'], 'RecordDeleteSuccess')
);

export const getDeleteModalMessageTranslation = createSelector(getTranslations, (translations) =>
  _.get(translations, ['Form', 'grdDeleteConfirm'], 'grdDeleteConfirm')
);

export const getIsDeleteModalOpen = createSelector(
  getMasterSetupPage,
  (masterSetupPage) => masterSetupPage?.isDeleteModalOpen
);

export const getIsLoading = createSelector(
  getMasterSetupPage,
  (masterSetupPage) => masterSetupPage?.isLoading
);

export const getIsSmartDocuments = createSelector(getSavedSearchResults, (savedSearchResults) =>
  SMART_DOCUMENTS_ENTITIES.includes(savedSearchResults.currentEntityViewName)
);

export const getManageRights = createSelector(
  getMasterSetupPage,
  (masterSetupPage) => masterSetupPage?.manageRights
);

export const checkManageRightByEntityName = createSelector(
  [getManageRights, getComponentProps],
  (manageRights, { entityName }) => !!manageRights[entityName]
);
